import moment from "moment";
import React from "react";
import { Col, Row } from "react-grid-system";

import { buildFullName } from "../../../../../../util/formatHelpers";
import PageCardRowHeader from "../../../../../General/PageCardRowHeader";
import FieldSection, { Field } from "../../ResponsesTab/FieldSection";

function ContractStatusField({ contract }) {
  const determineStatusText = () => {
    if (!contract.attributes.activatedAt) return "Pending Pre-Assignment";

    if (moment.parseZone(contract.attributes.endedAt).isBefore(moment()))
      return "Closed";

    return "Open";
  };

  return <Field label="Contract Status">{determineStatusText()}</Field>;
}

function JobTitleField({ jobcast }) {
  return <Field label="Job Title">{jobcast.attributes.title}</Field>;
}

function StartDateField({ contract }) {
  return (
    <Field label="Start Date">
      {moment
        .parseZone(contract.attributes.scheduledStartDate)
        .format("MMM Do, YYYY")}
    </Field>
  );
}

function EndDateField({ contract }) {
  return (
    <Field label="End Date">
      {contract.attributes.endedAt
        ? moment.parseZone(contract.attributes.endedAt).format("MMM Do, YYYY")
        : "N/A"}
    </Field>
  );
}

function ContractLength({ contract }) {
  const start = moment.parseZone(contract.attributes.scheduledStartDate);
  let length = -1;

  if (contract.attributes.endedAt) {
    if (moment().isAfter(start)) {
      length = moment
        .parseZone(contract.attributes.endedAt)
        .diff(start, "days");
    }
  } else {
    length = moment().diff(start, "days");
  }

  let content;
  if (length < 0) content = "N/A";
  else if (length <= 1) content = "1 Day";
  else content = `${length} Days`;

  return <Field label="Contract Length">{content}</Field>;
}

function ScheduledEndDate({ contract }) {
  return (
    <Field label="Tentative End Date">
      {moment
        .parseZone(contract.attributes.scheduledEndDate)
        .format("MMM Do, YYYY")}
    </Field>
  );
}

function PrimaryApproversField({ contract }) {
  const approvers = contract.attributes.primaryClientIndividuals;
  return (
    <Field label="Client Managers">
      {approvers && approvers.length > 0
        ? approvers.map((approver) => (
            <div key={approver.id}>{approver.name} {approver.registered ? "" : "(Pending)"}</div>
          ))
        : "None selected"}
    </Field>
  );
}

function TimesheetSubmittersField({ contract }) {
  const submitters = contract.attributes.supplierIndividuals;
  return (
    <Field label="Staffing Firm Managers">
      {submitters && submitters.length > 0
        ? submitters.map((submitter) => (
            <div key={submitter.id}>
              {buildFullName(submitter.firstName, submitter.lastName)}
            </div>
          ))
        : "None selected"}
    </Field>
  );
}

function BillRateField({ candidate }) {
  return (
    <Field label="Std. Bill Rate">
      {`$${parseFloat(candidate.attributes.hourlyBillRate)} / hr`}
    </Field>
  );
}

function PayRateField({ candidate }) {
  return (
    <Field label="Std. Pay Rate">
      {candidate.attributes.hourlyPayRate
        ? `$${candidate.attributes.hourlyPayRate.toFixed(2)} / hr`
        : "Not Provided by Agency"}
    </Field>
  );
}

function MarkupPercentageField({ candidate }) {
  return (
    <Field label="Markup Percentage">
      {`${(candidate.attributes.markupPercentage * 100).toFixed(2)}%`}
    </Field>
  );
}

function OTBillRateField({ candidate }) {
  return (
    <Field label="OT Bill Rate">
      {`$${parseFloat(candidate.attributes.hourlyOvertimeBillRate || 0).toFixed(
        2
      )} / hr`}
    </Field>
  );
}

function StaffingFirmField({ candidate }) {
  return <Field label="Staffing Firm">{candidate.attributes.agencyName}</Field>;
}

function TimesheetSubmissionResponsibility({ contract, employer }) {
  const setting =
    contract.attributes.hasEmployerSubmittedTimesheets === true
      ? employer
        ? "My Company"
        : "Client"
      : "Staffing Firm";
  return <Field label="Timesheet Submission Responsibility">{setting}</Field>;
}

function LocationField({ contract }) {
  return (
    <Field label="Location">
      {contract.attributes.primaryWorkLocation || "N/A"}
    </Field>
  );
}

function JobcastBrand({ jobcast }) {
  return <Field label="Brand">{jobcast.attributes.organizationName}</Field>;
}

function PONumberField({ contract }) {
  return (
    <Field label="PO Number">
      {contract.attributes.poNumber || "N/A"}
    </Field>
  );
}

function ContractInformation({ candidate, contract, jobcast, employer }) {
  return (
    <>
      <PageCardRowHeader title="Contract Information" />
      <FieldSection>
        <Row>
          <Col xs={3}>
            <ContractStatusField contract={contract} />
          </Col>
          <Col xs={3}>
            <JobTitleField jobcast={jobcast} />
          </Col>
          <Col xs={3}>
            <LocationField contract={contract} />
          </Col>
          <Col xs={3}>
            <JobcastBrand jobcast={jobcast} contract={contract} />
          </Col>
        </Row>
        <Row>
          <Col xs={3}>
            <StartDateField contract={contract} />
          </Col>
          <Col xs={3}>
            <EndDateField contract={contract} />
          </Col>
          <Col xs={3}>
            <ContractLength contract={contract} />
          </Col>
          <Col xs={3}>
            <ScheduledEndDate contract={contract} />
          </Col>
        </Row>
        <Row>
          {employer && (
            <Col xs={3}>
              <StaffingFirmField candidate={candidate} />
            </Col>
          )}
          <Col xs={3}>
            <BillRateField candidate={candidate} />
          </Col>
          {!candidate.attributes.usesPayRateMarkup && (
            <Col xs={3}>
              <OTBillRateField candidate={candidate} />
            </Col>
          )}
          <Col xs={3}>
            <PayRateField candidate={candidate} />
          </Col>
          {candidate.attributes.usesPayRateMarkup && (
            <Col xs={3}>
              <OTBillRateField candidate={candidate} />
            </Col>
          )}
        </Row>
        {candidate.attributes.usesPayRateMarkup && (
          <Row>
            <Col xs={3}>
              <MarkupPercentageField candidate={candidate} />
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={6}>
            <PrimaryApproversField contract={contract} />
          </Col>
          <Col xs={6}>
            <TimesheetSubmittersField contract={contract} />
          </Col>
          <Col xs={6}>
            <TimesheetSubmissionResponsibility
              contract={contract}
              employer={employer}
            />
          </Col>
          {contract.attributes.poNumber && (
            <Col xs={6}>
              <PONumberField contract={contract} />
            </Col>
          )}
        </Row>
      </FieldSection>
    </>
  );
}

export default ContractInformation;
